import { AlertCircle, ChevronLeft, ChevronRight, FileText, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  createStrapiEntry,
  deleteStrapiEntry,
  listStrapiContentTypes,
  listStrapiEntries,
  updateStrapiEntry,
  type StrapiContentType,
  type StrapiEntriesResponse,
  type StrapiEntry,
} from "@/services/strapi";

export function ContentViewPanel() {
  const [contentTypes, setContentTypes] = React.useState<StrapiContentType[]>([]);
  const [entriesResponse, setEntriesResponse] = React.useState<StrapiEntriesResponse | null>(null);
  const [selectedType, setSelectedType] = React.useState<string>("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [loadingTypes, setLoadingTypes] = React.useState(false);
  const [loadingEntries, setLoadingEntries] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingEntry, setEditingEntry] = React.useState<StrapiEntry | null>(null);
  const [formValues, setFormValues] = React.useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = React.useState(false);

  const loadTypes = React.useCallback(async () => {
    setLoadingTypes(true);
    setError(null);
    try {
      const data = await listStrapiContentTypes();
      const apiTypes = data.filter((t) => t.uid?.startsWith("api::"));
      setContentTypes(apiTypes);
      if (apiTypes.length > 0 && !selectedType) {
        setSelectedType(apiTypes[0].uid);
      }
    } catch (err) {
      setContentTypes([]);
      setError(extractErrorMessage(err, "无法连接到 Strapi CMS 服务，请确认服务已启动。"));
    } finally {
      setLoadingTypes(false);
    }
  }, [selectedType]);

  const loadEntries = React.useCallback(async (contentType: string, pageNum: number, size: number) => {
    if (!contentType) return;
    setLoadingEntries(true);
    setError(null);
    try {
      const data = await listStrapiEntries(contentType, pageNum, size);
      setEntriesResponse(data);
    } catch (err) {
      setEntriesResponse(null);
      setError(extractErrorMessage(err, "加载内容条目失败。"));
    } finally {
      setLoadingEntries(false);
    }
  }, []);

  React.useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  React.useEffect(() => {
    setPage(1);
  }, [selectedType]);

  React.useEffect(() => {
    void loadEntries(selectedType, page, pageSize);
  }, [selectedType, page, pageSize, loadEntries]);

  const selectedTypeDef = contentTypes.find((t) => t.uid === selectedType);
  const attributes = selectedTypeDef?.schema?.attributes ?? {};
  const attributeNames = Object.keys(attributes).filter(
    (key) => key !== "createdAt" && key !== "updatedAt" && key !== "publishedAt",
  );

  const entries = entriesResponse?.data ?? [];
  const pagination = entriesResponse?.meta?.pagination;
  const totalPages = pagination?.pageCount ?? 1;
  const totalCount = pagination?.total ?? 0;

  const openCreate = () => {
    setEditingEntry(null);
    setFormValues(Object.fromEntries(attributeNames.map((name) => [name, ""])));
    setDialogOpen(true);
  };

  const openEdit = (entry: StrapiEntry) => {
    setEditingEntry(entry);
    const attrs = entry.attributes ?? entry;
    setFormValues(
      Object.fromEntries(
        attributeNames.map((name) => {
          const value = attrs[name];
          return [name, value === null || value === undefined ? "" : value];
        }),
      ),
    );
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedType) return;
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        attributeNames.map((name) => {
          const attr = attributes[name];
          let value = formValues[name];
          if (attr?.type === "integer" || attr?.type === "biginteger") {
            value = value === "" ? null : Number(value);
          } else if (attr?.type === "decimal" || attr?.type === "float" || attr?.type === "number") {
            value = value === "" ? null : Number(value);
          } else if (attr?.type === "boolean") {
            value = Boolean(value);
          }
          return [name, value];
        }),
      );
      if (editingEntry) {
        await updateStrapiEntry(selectedType, editingEntry.id ?? editingEntry.documentId ?? "", payload);
      } else {
        await createStrapiEntry(selectedType, payload);
      }
      setDialogOpen(false);
      await loadEntries(selectedType, page, pageSize);
    } catch (err) {
      setError(extractErrorMessage(err, editingEntry ? "更新条目失败。" : "新增条目失败。"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entry: StrapiEntry) => {
    if (!selectedType) return;
    const entryId = entry.id ?? entry.documentId;
    if (!entryId) return;
    if (!window.confirm("确定删除该内容条目？此操作不可撤销。")) return;
    try {
      await deleteStrapiEntry(selectedType, entryId);
      await loadEntries(selectedType, page, pageSize);
    } catch (err) {
      setError(extractErrorMessage(err, "删除条目失败。"));
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            内容管理（Strapi CMS）
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void loadTypes()} disabled={loadingTypes}>
              <RefreshCw className={cn("mr-1 h-3.5 w-3.5", loadingTypes && "animate-spin")} />
              刷新
            </Button>
            <Button size="sm" onClick={openCreate} disabled={!selectedType}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              新增条目
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 overflow-hidden">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Strapi 服务暂不可用</p>
              <p className="text-xs opacity-90">{error}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">内容类型</label>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              disabled={loadingTypes || contentTypes.length === 0}
              className="h-8 min-w-[10rem] rounded-md border bg-background px-2 text-xs"
            >
              {contentTypes.length === 0 && <option value="">无可用类型</option>}
              {contentTypes.map((type) => (
                <option key={type.uid} value={type.uid}>
                  {type.schema?.displayName || type.uid}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">每页</label>
            <Input
              type="number"
              min={10}
              max={100}
              value={pageSize}
              onChange={(e) => setPageSize(Math.max(10, Math.min(100, Number(e.target.value) || 25)))}
              className="h-8 w-16 text-xs"
            />
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-md border">
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  {attributeNames.length === 0 ? (
                    <TableHead>暂无字段</TableHead>
                  ) : (
                    <>
                      {attributeNames.map((col) => (
                        <TableHead key={col}>{col}</TableHead>
                      ))}
                      <TableHead className="w-24">操作</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingEntries ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(2, attributeNames.length + 2)} className="text-center text-muted-foreground">
                      加载中...
                    </TableCell>
                  </TableRow>
                ) : entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(2, attributeNames.length + 2)} className="text-center text-muted-foreground">
                      暂无内容条目
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => {
                    const attrs = entry.attributes ?? entry;
                    return (
                      <TableRow key={entry.id ?? entry.documentId}>
                        <TableCell className="text-xs">{entry.id ?? entry.documentId}</TableCell>
                        {attributeNames.map((col) => (
                          <TableCell key={col} className="max-w-xs truncate text-xs">
                            {formatCellValue(attrs[col])}
                          </TableCell>
                        ))}
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(entry)}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              onClick={() => void handleDelete(entry)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between border-t px-4 py-2 text-xs">
            <span className="text-muted-foreground">
              共 {totalCount} 条，第 {page} / {totalPages} 页
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loadingEntries}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loadingEntries}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? "编辑条目" : "新增条目"}</DialogTitle>
            <DialogDescription>
              在「{selectedTypeDef?.schema?.displayName || selectedType}」中{editingEntry ? "更新" : "创建"}内容。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {attributeNames.length === 0 ? (
              <p className="text-sm text-muted-foreground">当前内容类型无字段，无法{editingEntry ? "编辑" : "新增"}。</p>
            ) : (
              attributeNames.map((name) => {
                const attr = attributes[name];
                const type = attr?.type as string | undefined;
                return (
                  <div key={name} className="grid gap-2">
                    <Label className="text-xs">{name}</Label>
                    {type === "boolean" ? (
                      <Input
                        type="checkbox"
                        checked={Boolean(formValues[name])}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [name]: e.target.checked }))}
                        className="h-4 w-4"
                      />
                    ) : type === "integer" || type === "biginteger" ? (
                      <Input
                        type="number"
                        step={1}
                        value={String(formValues[name] ?? "")}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [name]: e.target.value === "" ? "" : Number(e.target.value),
                          }))
                        }
                        className="h-8 text-xs"
                      />
                    ) : type === "decimal" || type === "float" || type === "number" ? (
                      <Input
                        type="number"
                        value={String(formValues[name] ?? "")}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [name]: e.target.value === "" ? "" : Number(e.target.value),
                          }))
                        }
                        className="h-8 text-xs"
                      />
                    ) : type === "richtext" || type === "blocks" || type === "json" ? (
                      <textarea
                        value={String(formValues[name] ?? "")}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [name]: e.target.value }))}
                        rows={4}
                        className="rounded-md border bg-background px-3 py-2 text-xs"
                      />
                    ) : (
                      <Input
                        value={String(formValues[name] ?? "")}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, [name]: e.target.value }))}
                        className="h-8 text-xs"
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={submitting || attributeNames.length === 0}>
              {submitting ? "保存中..." : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "object") {
    if (Array.isArray(value)) return `[${value.length}]`;
    if (value && "data" in value) return "[关联]";
    return JSON.stringify(value);
  }
  return String(value);
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message || fallback;
  }
  return fallback;
}
